import React, { useCallback } from 'react';
import MenuItem from './MenuItem';
import { AddonPill } from './AddonPills';
import { isCompactSection, normalizeItem } from '../utils/menuHelpers';
import InlineItemEditor from '../../../Components/AdminEdit/InlineItemEditor';
import { CategoryTitleBar, countLabel } from '../../../Components/AdminEdit/CategoryTools';

const SECTION_ICONS = {
  'Dosa / Pesarattu / Ragi Dosa': '🫓',
  'Street Bites': '🥙',
  'Appetizers': '🍢',
  'Indo Chinese': '🥢',
  'Biryanis & Pulaos': '🍛',
  'Curries': '🫕',
  'Breads': '🫓',
  'Chaats': '🌮',
  'Beverages & Desserts': '🥤',
};

const VEG_NONVEG_SECTIONS = new Set([
  'Appetizers', 'Indo Chinese', 'Biryanis & Pulaos', 'Curries',
]);

/**
 * Category-level controls (rename / delete / reorder), shown above the rows
 * while editing. Renders nothing unless FoodMenu passed the handlers, so the
 * bar can't appear on a page that has no way to act on it.
 */
const CategoryEditBar = ({ title, hint, onRename, onDelete, onMoveUp, onMoveDown, takenNames }) => {
  if (!onRename && !onDelete) return null;
  return (
    <CategoryTitleBar
      title={title}
      hint={hint}
      onRename={onRename}
      onDelete={onDelete}
      onMoveUp={onMoveUp}
      onMoveDown={onMoveDown}
      takenNames={takenNames}
    />
  );
};


/**
 * Price-matrix section — one row per style, one price column per base.
 *
 * The crepe bases share an identical style list and differ only in price, so a
 * flat list repeated every name once per base. Rows are `[style, ...prices]`,
 * the shape catering tables already use, so InlineItemEditor edits them as-is.
 */
const PriceMatrix = ({ section, onSelect, isEditing, onUpdate, ...catProps }) => {
  const { title, priceColumns = [], columnNotes, rows = [], note, addons, nonvegAddons } = section;
  const icon = section.icon || SECTION_ICONS[title] || '🫓';
  const [newRowIdx, setNewRowIdx] = React.useState(null);

  const setRows = (next) => onUpdate?.({ ...section, rows: next });

  // The editor hands back strings; store numbers so the published JSON keeps one
  // type and the search index can read a price off an edited row.
  const toRow = ([style, ...prices]) => [
    String(style).trim(),
    ...prices.map((p) => {
      const n = parseFloat(p);
      return p === '' || Number.isNaN(n) ? null : Math.round(n * 100) / 100;
    }),
  ];
  const saveRow = (i, updated) => setRows(rows.map((r, j) => (j === i ? toRow(updated) : r)));
  const deleteRow = (i) => setRows(rows.filter((_, j) => j !== i));
  const moveRow = (i, dir) => {
    const arr = [...rows];
    const to = i + dir;
    if (to < 0 || to >= arr.length) return;
    [arr[i], arr[to]] = [arr[to], arr[i]];
    setRows(arr);
  };
  const addRow = () => {
    const next = [...rows, ['', ...priceColumns.map(() => '')]];
    setRows(next);
    setNewRowIdx(next.length - 1);
  };

  const dishName = (style, base) => (style === 'Plain' ? base : `${style} ${base}`);
  const money = (v) => (typeof v === 'number' ? `$${v.toFixed(2)}` : '—');

  return (
    <div className="fm-card">
      <div className="fm-card__header">
        <span className="fm-card__icon">{icon}</span>
        <h2 className="fm-card__title">{title}</h2>
      </div>
      {note && <div className="fm-card__note">{note}</div>}

      {isEditing ? (
        <div className="ae-section">
          <CategoryEditBar
            title={title}
            hint={`${countLabel(rows.length, 'style')} × ${priceColumns.length} prices`}
            {...catProps}
          />
          <div className="ae-section-title">{title} — style, then one price per base</div>
          <div className="ae-matrix-head">
            <span className="ae-matrix-head__name">Style</span>
            {priceColumns.map((c) => <span className="ae-matrix-head__col" key={c}>{c}</span>)}
          </div>
          {rows.map((r, i) => (
            <InlineItemEditor
              key={i}
              item={r}
              mode="table"
              colLabels={priceColumns}
              autoEdit={newRowIdx === i}
              onSave={(updated) => { saveRow(i, updated); setNewRowIdx(null); }}
              onDelete={() => { deleteRow(i); setNewRowIdx(null); }}
              onMoveUp={i > 0 ? () => moveRow(i, -1) : undefined}
              onMoveDown={i < rows.length - 1 ? () => moveRow(i, 1) : undefined}
            />
          ))}
          <button className="ae-add-btn" onClick={addRow}>+ Add Style</button>
        </div>
      ) : (
        <>
          <div
            className="fm-matrix"
            style={{ '--fm-matrix-cols': priceColumns.length }}
          >
            <div className="fm-matrix__head">
              <span className="fm-matrix__style">Style</span>
              {priceColumns.map((c) => (
                <span className="fm-matrix__col" key={c}>{c}</span>
              ))}
            </div>
            {rows.map(([style, ...prices], i) => (
              <div className="fm-matrix__row" key={`${style}-${i}`}>
                <span className="fm-matrix__style">{style}</span>
                {priceColumns.map((base, ci) => (
                  <button
                    type="button"
                    className="fm-matrix__col fm-matrix__cell"
                    key={base}
                    disabled={typeof prices[ci] !== 'number'}
                    aria-label={`${dishName(style, base)}, ${money(prices[ci])}`}
                    onClick={() => onSelect?.({
                      name: dishName(style, base),
                      desc: columnNotes?.[base] || '',
                    })}
                  >
                    {money(prices[ci])}
                  </button>
                ))}
              </div>
            ))}
          </div>

          {columnNotes && (
            <dl className="fm-matrix__legend">
              {priceColumns.filter((c) => columnNotes[c]).map((c) => (
                <React.Fragment key={c}>
                  <dt className="fm-matrix__legend-term">{c}</dt>
                  <dd className="fm-matrix__legend-desc">{columnNotes[c]}</dd>
                </React.Fragment>
              ))}
            </dl>
          )}
        </>
      )}

      {addons?.length > 0 && (
        <div className="fm-card__addons-strip">
          <span className="fm-addons__badge fm-addons__badge--addon">VEG ADD-ONS</span>
          {addons.map((a, i) => (
            <AddonPill key={i} addon={a} variant="veg" onSelect={onSelect} />
          ))}
        </div>
      )}
      {/* Kept a separate strip: merging the two marked egg, chicken and lamb
          with the green veg dot. */}
      {nonvegAddons?.length > 0 && (
        <div className="fm-card__nonveg-strip">
          <span className="fm-addons__badge fm-addons__badge--nonveg" style={{ fontSize: 10 }}>
            NON-VEG ADD-ONS
          </span>
          {nonvegAddons.map((a, i) => (
            <AddonPill key={i} addon={a} variant="nonveg" onSelect={onSelect} />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * A single menu category section card.
 * When isEditing=true (admin/manager), shows inline CRUD controls.
 */
const ColumnSection = ({ section, onSelect, isEditing = false, onUpdate, canEditPrice = false, ...catProps }) => {
  const { title, columns = [], nonvegAddons, addons, note } = section;

  const compact = isCompactSection(title);
  const icon = section.icon || SECTION_ICONS[title] || '🍽️';
  // Memoised: `columns[0] || []` is a fresh array whenever a column is absent,
  // which re-ran the mobile-list memo on every render.
  const firstCol = React.useMemo(() => columns[0] || [], [columns]);
  const secondCol = React.useMemo(() => columns[1] || [], [columns]);
  // `dietColumns` is set on categories built in the editor; the named set
  // covers the sections that predate it.
  const dietSplit = section.dietColumns === true || VEG_NONVEG_SECTIONS.has(title);
  const showDietDot = dietSplit && firstCol.length > 0 && secondCol.length > 0;

  // Merge both columns into a single list for mobile view
  const mobileItems = React.useMemo(() => {
    if (!showDietDot) {
      return [...firstCol, ...secondCol].map((raw) => ({ ...normalizeItem(raw), diet: null }));
    }
    const veg = firstCol.map((raw) => ({ ...normalizeItem(raw), diet: 'veg' }));
    const nonveg = secondCol.map((raw) => ({ ...normalizeItem(raw), diet: 'nonveg' }));
    return [...veg, ...nonveg];
  }, [firstCol, secondCol, showDietDot]);

  /* ── Admin edit helpers ── */
  const updateColumn = useCallback((colIdx, newItems) => {
    const newColumns = columns.map((col, i) => (i === colIdx ? newItems : col));
    onUpdate?.({ ...section, columns: newColumns });
  }, [columns, section, onUpdate]);

  const handleSaveItem = useCallback((colIdx, itemIdx, updated) => {
    const newCol = columns[colIdx].map((it, i) => (i === itemIdx ? updated : it));
    updateColumn(colIdx, newCol);
  }, [columns, updateColumn]);

  const handleDeleteItem = useCallback((colIdx, itemIdx) => {
    const newCol = columns[colIdx].filter((_, i) => i !== itemIdx);
    updateColumn(colIdx, newCol);
  }, [columns, updateColumn]);

  const handleMoveItem = useCallback((colIdx, itemIdx, dir) => {
    const col = [...columns[colIdx]];
    const to = itemIdx + dir;
    if (to < 0 || to >= col.length) return;
    [col[itemIdx], col[to]] = [col[to], col[itemIdx]];
    updateColumn(colIdx, col);
  }, [columns, updateColumn]);

  const [newItemPos, setNewItemPos] = React.useState(null);
  const handleAddItem = useCallback((colIdx) => {
    const newItem = { name: '', desc: '' };
    const newCol = [...(columns[colIdx] || []), newItem];
    updateColumn(colIdx, newCol);
    setNewItemPos({ colIdx, itemIdx: newCol.length - 1 });
  }, [columns, updateColumn]);

  return (
    <div className={`fm-card ${compact ? 'fm-card--compact' : ''}`}>
      <div className="fm-card__header">
        <span className="fm-card__icon">{icon}</span>
        <h2 className="fm-card__title">{title}</h2>
      </div>

      {note && <div className="fm-card__note">{note}</div>}

      {/* ── Admin Edit View ── */}
      {isEditing ? (
        <div className="ae-section">
          <CategoryEditBar
            title={title}
            hint={countLabel(columns.flat().length)}
            {...catProps}
          />
          {/* A section published without any column array would otherwise render
              no "+ Add Item" at all, leaving it permanently uneditable. */}
          {(columns.length ? columns : [[]]).map((col, colIdx) => (
            <div key={colIdx} className="ae-group">
              <div className="ae-group-label">
                {columns.length > 1
                  ? (dietSplit
                    ? (colIdx === 0 ? '🟢 Veg Items' : '🔴 Non-Veg Items')
                    : `Column ${colIdx + 1}`)
                  : 'Items'}
              </div>
              {col.map((item, itemIdx) => (
                <InlineItemEditor
                  key={itemIdx}
                  item={item}
                  mode="menu"
                  canEditPrice={canEditPrice}
                  autoEdit={newItemPos?.colIdx === colIdx && newItemPos?.itemIdx === itemIdx}
                  onSave={(updated) => { handleSaveItem(colIdx, itemIdx, updated); setNewItemPos(null); }}
                  onDelete={() => { handleDeleteItem(colIdx, itemIdx); setNewItemPos(null); }}
                  onMoveUp={itemIdx > 0 ? () => handleMoveItem(colIdx, itemIdx, -1) : undefined}
                  onMoveDown={itemIdx < col.length - 1 ? () => handleMoveItem(colIdx, itemIdx, 1) : undefined}
                />
              ))}
              <button className="ae-add-btn" onClick={() => handleAddItem(colIdx)}>
                + Add Item
              </button>
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* ── Desktop two-column (or compact single-column) ── */}
          <div className={`fm-card__body fm-card__desktop ${compact ? '' : 'fm-card__body--columns'}`}>
            {compact ? (
              <div className="fm-card__list">
                {columns.flat().map((item, i) => (
                  <MenuItem key={i} item={item} onSelect={onSelect} />
                ))}
              </div>
            ) : (
              <>
                <div className="fm-card__col">
                  {firstCol.map((item, i) => (
                    <MenuItem key={i} item={item} onSelect={onSelect} />
                  ))}
                </div>
                <div className="fm-card__col">
                  {secondCol.map((item, i) => (
                    <MenuItem key={i} item={item} onSelect={onSelect} forceNonvegCheck />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* ── Mobile unified list ── */}
          <div className="fm-mobile-rails">
            <div className="fm-mobile-list" role="list">
              {mobileItems.map((item, i) => (
                <article
                  key={`${item.name}-${i}`}
                  className="fm-mobile-list-item"
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelect?.(item)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect?.(item); }
                  }}
                >
                  {item.diet && (
                    <span className={`fm-diet-dot fm-diet-dot--${item.diet}`} aria-label={item.diet === 'veg' ? 'Vegetarian' : 'Non-Vegetarian'} />
                  )}
                  <div className="fm-mobile-list-item__body">
                    <h3 className="fm-mobile-list-item__name">{item.name}</h3>
                    {item.desc && <p className="fm-mobile-list-item__desc">{item.desc}</p>}
                  </div>
                  {item.price != null && (
                    <span className="fm-mobile-list-item__price">${item.price.toFixed(2)}</span>
                  )}
                </article>
              ))}
            </div>
          </div>

          {/* Non-veg add-ons strip */}
          {nonvegAddons?.length > 0 && (
            <div className="fm-card__nonveg-strip">
              <span className="fm-addons__badge fm-addons__badge--nonveg" style={{ fontSize: 10 }}>
                NON-VEG ADD-ONS
              </span>
              {nonvegAddons.map((addon, i) => (
                <AddonPill key={i} addon={addon} variant="nonveg" onSelect={onSelect} />
              ))}
            </div>
          )}

          {/* Generic add-ons strip (e.g. Schezwan +$1) */}
          {addons?.length > 0 && (
            <div className="fm-card__addons-strip">
              <span className="fm-addons__badge fm-addons__badge--addon">ADD-ONS</span>
              {addons.map((addon, i) => {
                const name = typeof addon === 'string' ? addon : addon?.name || '';
                const price = typeof addon === 'object' ? addon?.price : null;
                return (
                  <span key={i} className="fm-pill">
                    <span className="fm-pill__dot fm-pill__dot--addon" />
                    {name}{price != null ? <span className="fm-pill__price"> +${price.toFixed(2)}</span> : ''}
                  </span>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

/**
 * A menu category section.
 *
 * Two shapes exist: the usual column list, and the crepe price matrix (`rows` +
 * `priceColumns`). Dispatching here — rather than returning early inside
 * ColumnSection — keeps each renderer's hooks unconditional.
 */
const SectionCard = (props) => (
  Array.isArray(props.section?.priceColumns) && Array.isArray(props.section?.rows)
    ? <PriceMatrix {...props} />
    : <ColumnSection {...props} />
);

export default React.memo(SectionCard);
